<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import integrationAPI from 'dashboard/api/integrations';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

/**
 * @typedef {Object} KlaviyoSubscriptionState
 * @property {'email'|'sms'} channel
 * @property {string} message_type
 * @property {string|null} remote_consent
 * @property {boolean|null} remote_can_receive
 * @property {string} sync_status
 * @property {Array} remote_suppression
 * @property {Array} remote_list_suppressions
 * @property {string|null} provider_error
 */

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const REFRESH_INTERVAL_MS = 3000;
const MAX_REFRESH_ATTEMPTS = 20;

const { t } = useI18n();
const isLoading = ref(false);
const submittingChannel = ref('');
const profilePayload = ref(null);
const errorMessage = ref('');
const isKlaviyoUnavailable = ref(false);
const refreshTimer = ref(null);
const remainingRefreshAttempts = ref(0);

const subscriptionStates = computed(
  () => profilePayload.value?.subscription_states || []
);
const hasLoadedProfile = computed(() => Boolean(profilePayload.value));
const hasAvailableKlaviyoIntegration = computed(
  () => profilePayload.value?.integration_available !== false
);
const subscriptionStatusLabels = computed(() => ({
  subscribed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.SUBSCRIBED'),
  unsubscribed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.UNSUBSCRIBED'),
  neverSubscribed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.NEVER_SUBSCRIBED'),
  pendingOutbound: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.PENDING_OUTBOUND'),
  submitted: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.SUBMITTED'),
  pendingConfirmation: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.PENDING_CONFIRMATION'
  ),
  requiresAction: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.REQUIRES_ACTION'),
  conflict: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.CONFLICT'),
  failed: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.FAILED'),
  unknown: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.STATUS.UNKNOWN'),
}));
const subscriptionActionLabels = computed(() => ({
  subscribe: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.ACTIONS.SUBSCRIBE'),
  resubscribe: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.ACTIONS.RESUBSCRIBE'),
  unsubscribe: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.ACTIONS.UNSUBSCRIBE'),
}));
const subscriptionTooltipKeys = computed(() => ({
  subscribed: 'SUBSCRIBED',
  unsubscribed: 'UNSUBSCRIBED',
  neverSubscribed: 'NEVER_SUBSCRIBED',
  pendingOutbound: 'PENDING_OUTBOUND',
  submitted: 'SUBMITTED',
  pendingConfirmation: 'PENDING_CONFIRMATION',
  requiresAction: 'REQUIRES_ACTION',
  conflict: 'CONFLICT',
  failed: 'FAILED',
  unknown: 'UNKNOWN',
}));
const subscriptionTooltipLabels = computed(() => ({
  SUBSCRIBED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.SUBSCRIBED'),
  UNSUBSCRIBED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.UNSUBSCRIBED'),
  NEVER_SUBSCRIBED: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.NEVER_SUBSCRIBED'
  ),
  PENDING_OUTBOUND: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.PENDING_OUTBOUND'
  ),
  SUBMITTED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.SUBMITTED'),
  PENDING_CONFIRMATION: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.PENDING_CONFIRMATION'
  ),
  REQUIRES_ACTION: t(
    'CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.REQUIRES_ACTION'
  ),
  CONFLICT: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.CONFLICT'),
  FAILED: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.FAILED'),
  UNKNOWN: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.UNKNOWN'),
}));

/**
 * Finds the current marketing subscription state for a channel.
 *
 * @param {string} channel Email or SMS channel.
 * @returns {KlaviyoSubscriptionState|undefined}
 */
function subscriptionStateFor(channel) {
  return subscriptionStates.value.find(
    state => state.channel === channel && state.message_type === 'marketing'
  );
}

const subscriptionChannels = computed(() =>
  [
    {
      channel: 'email',
      label: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.EMAIL'),
      identifier: props.selectedContact?.email,
      icon: 'i-lucide-mail',
    },
    {
      channel: 'sms',
      label: t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.SMS'),
      identifier: props.selectedContact?.phoneNumber,
      icon: 'i-lucide-message-square',
    },
  ]
    .map(item => ({
      ...item,
      state: subscriptionStateFor(item.channel),
    }))
    .filter(
      item =>
        hasLoadedProfile.value &&
        hasAvailableKlaviyoIntegration.value &&
        (item.identifier || item.state)
    )
);
const hasSubscriptionChannels = computed(
  () => subscriptionChannels.value.length > 0
);
const shouldRenderControls = computed(() => {
  if (isKlaviyoUnavailable.value) return false;
  if (isLoading.value || errorMessage.value || hasSubscriptionChannels.value) {
    return true;
  }

  return hasLoadedProfile.value && hasAvailableKlaviyoIntegration.value;
});

/**
 * Checks whether the CRM profile endpoint reports Klaviyo as unavailable.
 *
 * @param {unknown} error API error raised by Axios.
 * @returns {boolean}
 */
const isKlaviyoUnavailableResponse = error =>
  error?.response?.data?.code === 'klaviyo_disabled_for_account';

/**
 * Stops scheduled background refreshes for this contact.
 *
 * @returns {void}
 */
function stopRefreshPolling() {
  if (refreshTimer.value) window.clearTimeout(refreshTimer.value);
  refreshTimer.value = null;
  remainingRefreshAttempts.value = 0;
}

/**
 * Hides subscription controls when Klaviyo becomes unavailable for the account.
 *
 * @returns {void}
 */
const hideUnavailableKlaviyoControls = () => {
  profilePayload.value = null;
  isKlaviyoUnavailable.value = true;
  errorMessage.value = '';
  stopRefreshPolling();
};

/**
 * Loads locally cached Klaviyo subscription state for the selected contact.
 *
 * @returns {Promise<void>}
 */
const loadSubscriptionState = async () => {
  if (!props.selectedContact?.id) return;

  isLoading.value = true;
  errorMessage.value = '';
  isKlaviyoUnavailable.value = false;
  try {
    const response = await integrationAPI.getKlaviyoContactProfile(
      props.selectedContact.id
    );
    profilePayload.value = response.data;
  } catch (error) {
    if (isKlaviyoUnavailableResponse(error)) {
      hideUnavailableKlaviyoControls();
      return;
    }

    errorMessage.value = t('CONTACT_PANEL.CRM.ERROR');
  } finally {
    isLoading.value = false;
  }
};

/**
 * Maps backend sync statuses to computed label keys.
 *
 * @param {string} syncStatus Backend sync status.
 * @returns {string}
 */
function syncStatusLabelKey(syncStatus) {
  return (
    {
      pending_outbound: 'pendingOutbound',
      submitted: 'submitted',
      pending_confirmation: 'pendingConfirmation',
      requires_action: 'requiresAction',
      conflict: 'conflict',
      failed: 'failed',
    }[syncStatus] || ''
  );
}

/**
 * Checks if Klaviyo reports channel or list suppression.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {boolean}
 */
const hasSuppression = state =>
  Boolean(
    state?.remote_suppression?.length || state?.remote_list_suppressions?.length
  );

/**
 * Checks whether a subscription action is still being processed.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {boolean}
 */
const isSubscriptionActionPending = state =>
  ['pending_outbound', 'submitted', 'pending_confirmation'].includes(
    state?.sync_status
  );
const hasPendingSubscriptionAction = computed(() =>
  subscriptionStates.value.some(state => isSubscriptionActionPending(state))
);

/**
 * Builds the operator-facing status label for a subscription state.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {string}
 */
const subscriptionStatusLabel = state => {
  if (!state) return subscriptionStatusLabels.value.unknown;

  const syncStatusLabel =
    subscriptionStatusLabels.value[syncStatusLabelKey(state.sync_status)];
  if (syncStatusLabel) return syncStatusLabel;

  if (state.remote_consent === 'SUBSCRIBED' && state.remote_can_receive) {
    return subscriptionStatusLabels.value.subscribed;
  }
  if (state.remote_consent === 'UNSUBSCRIBED') {
    return subscriptionStatusLabels.value.unsubscribed;
  }
  if (hasSuppression(state))
    return subscriptionStatusLabels.value.requiresAction;

  if (state.remote_consent === 'NEVER_SUBSCRIBED') {
    return subscriptionStatusLabels.value.neverSubscribed;
  }
  return subscriptionStatusLabels.value.unknown;
};

/**
 * Builds the translation key suffix for the operator status tooltip.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {string}
 */
const subscriptionStatusTooltipKey = state => {
  if (!state) return subscriptionTooltipKeys.value.unknown;

  const syncStatusKey = syncStatusLabelKey(state.sync_status);
  if (syncStatusKey) return subscriptionTooltipKeys.value[syncStatusKey];

  if (state.remote_consent === 'SUBSCRIBED' && state.remote_can_receive) {
    return subscriptionTooltipKeys.value.subscribed;
  }
  if (state.remote_consent === 'UNSUBSCRIBED') {
    return subscriptionTooltipKeys.value.unsubscribed;
  }
  if (hasSuppression(state))
    return subscriptionTooltipKeys.value.requiresAction;
  if (state.remote_consent === 'NEVER_SUBSCRIBED') {
    return subscriptionTooltipKeys.value.neverSubscribed;
  }
  return subscriptionTooltipKeys.value.unknown;
};

/**
 * Builds operator-facing help text for a subscription status.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {string}
 */
const subscriptionStatusTooltip = state => {
  const baseTooltip =
    subscriptionTooltipLabels.value[subscriptionStatusTooltipKey(state)] ||
    subscriptionTooltipLabels.value.UNKNOWN;
  if (!state?.provider_error) return baseTooltip;

  return t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TOOLTIPS.PROVIDER_ERROR', {
    base: baseTooltip,
    error: state.provider_error,
  });
};

/**
 * Resolves the next explicit consent action for a channel state.
 *
 * @param {KlaviyoSubscriptionState|undefined} state Subscription state.
 * @returns {'subscribe'|'resubscribe'|'unsubscribe'}
 */
const nextSubscriptionAction = state => {
  if (state?.remote_consent === 'SUBSCRIBED' && state?.remote_can_receive) {
    return 'unsubscribe';
  }
  if (
    state?.remote_consent === 'UNSUBSCRIBED' ||
    state?.sync_status === 'requires_action' ||
    hasSuppression(state)
  ) {
    return 'resubscribe';
  }
  return 'subscribe';
};

/**
 * Refreshes pending subscription states without flashing the section spinner.
 *
 * @returns {Promise<void>}
 */
const refreshPendingSubscriptionState = async () => {
  if (!props.selectedContact?.id) {
    stopRefreshPolling();
    return;
  }

  try {
    const response = await integrationAPI.getKlaviyoContactProfile(
      props.selectedContact.id
    );
    profilePayload.value = response.data;
  } catch (error) {
    if (isKlaviyoUnavailableResponse(error)) {
      hideUnavailableKlaviyoControls();
      return;
    }

    stopRefreshPolling();
    return;
  }

  remainingRefreshAttempts.value -= 1;
  if (
    hasPendingSubscriptionAction.value &&
    remainingRefreshAttempts.value > 0
  ) {
    refreshTimer.value = window.setTimeout(
      refreshPendingSubscriptionState,
      REFRESH_INTERVAL_MS
    );
  } else {
    stopRefreshPolling();
  }
};

/**
 * Starts short polling while Klaviyo/provider readback is pending.
 *
 * @returns {void}
 */
function scheduleRefreshPolling() {
  if (refreshTimer.value) return;

  remainingRefreshAttempts.value = MAX_REFRESH_ATTEMPTS;
  refreshTimer.value = window.setTimeout(
    refreshPendingSubscriptionState,
    REFRESH_INTERVAL_MS
  );
}

/**
 * Sends an explicit operator subscription action to the backend.
 *
 * @param {Object} item Rendered subscription channel item.
 * @returns {Promise<void>}
 */
const submitSubscriptionAction = async item => {
  const consentAction = nextSubscriptionAction(item.state);
  submittingChannel.value = item.channel;
  try {
    const response = await integrationAPI.captureKlaviyoSubscriptionConsent(
      props.selectedContact.id,
      {
        channel: item.channel,
        consent_action: consentAction,
        evidence: {
          ui: 'edit_contact_details',
        },
      }
    );
    profilePayload.value = response.data;
    if (hasPendingSubscriptionAction.value) scheduleRefreshPolling();
    useAlert(t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.API.SUCCESS'));
  } catch (error) {
    if (isKlaviyoUnavailableResponse(error)) {
      hideUnavailableKlaviyoControls();
      return;
    }

    useAlert(
      error?.response?.data?.message ||
        t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.API.ERROR')
    );
  } finally {
    submittingChannel.value = '';
  }
};

onMounted(loadSubscriptionState);
onBeforeUnmount(stopRefreshPolling);

watch(
  () => props.selectedContact?.id,
  () => {
    profilePayload.value = null;
    stopRefreshPolling();
    loadSubscriptionState();
  }
);

watch(hasPendingSubscriptionAction, pending => {
  if (pending) {
    scheduleRefreshPolling();
  } else {
    stopRefreshPolling();
  }
});
</script>

<template>
  <div>
    <section v-if="shouldRenderControls" class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-3">
        <span class="py-1 text-sm font-medium text-n-slate-12">
          {{ t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.TITLE') }}
        </span>
        <Spinner v-if="isLoading" class="size-4 text-n-slate-10" />
      </div>

      <p v-if="errorMessage" class="text-sm text-n-ruby-9">
        {{ errorMessage }}
      </p>

      <div v-else-if="hasSubscriptionChannels" class="grid grid-cols-1 gap-3">
        <div
          v-for="item in subscriptionChannels"
          :key="item.channel"
          class="flex items-start justify-between gap-3 p-3 rounded-lg bg-n-alpha-2"
        >
          <div class="min-w-0">
            <div class="inline-flex items-center gap-2">
              <span class="size-4 text-n-slate-10" :class="item.icon" />
              <p class="text-sm font-medium text-n-slate-12">
                {{ item.label }}
              </p>
            </div>
            <p class="mt-1 text-xs break-all text-n-slate-10">
              {{ item.identifier || t('CONTACT_PANEL.NOT_AVAILABLE') }}
            </p>
            <div class="flex items-center gap-1.5 mt-1 text-xs text-n-slate-11">
              <p>
                {{ subscriptionStatusLabel(item.state) }}
              </p>
              <span
                class="mb-2 size-3.5 shrink-0 text-n-slate-10 i-lucide-info"
                :title="subscriptionStatusTooltip(item.state)"
                :aria-label="subscriptionStatusTooltip(item.state)"
              />
            </div>
            <p
              v-if="item.state?.provider_error"
              class="mt-1 text-xs break-words text-n-ruby-9"
            >
              {{ item.state.provider_error }}
            </p>
          </div>
          <Button
            v-if="!isSubscriptionActionPending(item.state)"
            xs
            faded
            slate
            class="shrink-0"
            :label="
              subscriptionActionLabels[nextSubscriptionAction(item.state)]
            "
            :is-loading="submittingChannel === item.channel"
            :disabled="Boolean(submittingChannel) || !item.identifier"
            @click="submitSubscriptionAction(item)"
          />
        </div>
      </div>

      <p v-else-if="!isLoading" class="text-sm text-n-slate-11">
        {{ t('CONTACT_PANEL.CRM.SUBSCRIPTIONS.EMPTY') }}
      </p>
    </section>
  </div>
</template>
